import React, { useState, useEffect, useContext, useRef } from 'react';
import { collection, updateDoc, doc, query, where, orderBy, limit, getDocs, writeBatch, startAfter, GeoPoint } from 'firebase/firestore';
import db from '../firebase'; // Adjust the import based on your project structure
import { FeedbackContext } from '../contexts/FeedbackContext';
import { extract } from '@extractus/article-extractor';
import { MetadataContext } from '../contexts/MetadataContext';
import { SpinnerContext } from '../contexts/SpinnerContext'; // Adjust the import based on your project structure
import OpenAI from 'openai'; // Adjust the import based on your project structure
import axios from 'axios'; // For fetching data from Diffbot
import * as cheerio from 'cheerio';
import { format } from 'date-fns';
import { Today } from '@mui/icons-material';

// Initialize a variable to track last Diffbot API call time
let lastDiffbotCallTime = 0;

// Delay function to pause execution
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const ProcessArticles = () => {
    //const { showSpinner, hideSpinner } = useContext(SpinnerContext);
    //const { showFeedback } = useContext(FeedbackContext);
    const { metadata } = useContext(MetadataContext);
    const [log, setLog] = useState({
        processedCount: 0,
        currentTitle: '',
        date: null,
        tags: [],
        tldr: '',
        action: ''
    });
    const [isProcessing, setIsProcessing] = useState(false); // State to track processing status
    const isProcessingRef = useRef(isProcessing); // Ref to keep track of isProcessing

    const logRef = useRef(null);
    const OPENAI_API_KEY = metadata.OPENAI_API_KEY || "";

    const openai = new OpenAI({
        apiKey: OPENAI_API_KEY,
        dangerouslyAllowBrowser: true //API key is exposed in the browser but only fetched from metadata when user logs in
    });

    const diffbotApiToken = '7376dfff82dd031ade7906d349a75617';

    // Update isProcessingRef whenever isProcessing changes
    useEffect(() => {
        isProcessingRef.current = isProcessing;
    }, [isProcessing]);

    const updateLog = (updates) => {
        if (typeof updates === 'function') {
            setLog(prevLog => {
                const newLog = updates(prevLog);
                if (logRef.current) {
                    logRef.current.scrollTop = logRef.current.scrollHeight;
                }
                return newLog;
            });
        } else {
            setLog(prevLog => {
                const newLog = { ...prevLog, ...updates };
                if (logRef.current) {
                    logRef.current.scrollTop = logRef.current.scrollHeight;
                }
                return newLog;
            });
        }
    };
    const fetchArticle = async (article) => {
        const pubDate = article.pubDate.toDate(); // Parse pubDate into a Date object
        updateLog({ currentTitle: article.title, date : pubDate });
        console.log("article date:", pubDate);

        // Check if articleData already exists
        if (article.article) {
            updateLog({ action: "Article content already exists. Skipping fetch." });
            console.log('Article content already exists. Using existing articleData.');
            return; // Skip fetching
        }

        try {
            updateLog({ action: "Extracting article content" });
            const extractedArticle = await extract(article.link);

            // Save the extracted article data to Firestore
            await updateDoc(doc(db, 'articles', article.id), {
                article: extractedArticle
            });

            // Update the article object
            article.article = extractedArticle;

            updateLog({ action: "Article content saved to Firebase" });
            console.log('Article data extracted and saved:', extractedArticle);
        } catch (error) {
            console.error('Error extracting article data:', error);
            updateLog({ action: 'Error extracting article data.' });
            // Optionally, update Firestore to mark the article as having an error
            await updateDoc(doc(db, 'articles', article.id), {
                extractionError: error.message
            });
        }
    };

    const processLatestArticleWithoutContent = async () => {
        // Query for the most recent article without 'articleData'
        const articlesQuery = query(
            collection(db, 'articles'),
            where('article', '==', {}),
            orderBy('pubDate', 'desc'),
            limit(1)
        );

        const querySnapshot = await getDocs(articlesQuery);
        if (querySnapshot.empty) {
            updateLog({ action: "No articles without content to process" });
            console.log('No articles without content to process');
            return;
        }

        const articleDoc = querySnapshot.docs[0];
        const articleData = articleDoc.data();
        articleData.id = articleDoc.id;

        const article = {
            ...articleData,
            articleData: articleData.articleData || null
        };

        console.log(`Processing article: ${article.title}`);

        await fetchArticle(article);

        updateLog({ action: "Article content extracted and saved" });
        console.log('Article content extracted and saved');
    };

    const addEmptyArticleToArticles = async () => {
        try {
            // Reference to the articles collection
            const articlesRef = collection(db, 'articles');

            // Fetch all articles
            const articlesSnapshot = await getDocs(articlesRef);

            // Initialize a batch
            const batch = writeBatch(db);
            let count = 0;

            articlesSnapshot.forEach((doc) => {
                const data = doc.data();
                // Check if 'article' field does not exist
                if (!data.hasOwnProperty('article')) {
                    // Add an empty 'TLDR' field
                    batch.update(doc.ref, { 'article': {} });
                    count++;
                }
            });

            if (count > 0) {
                // Commit the batch update
                await batch.commit();
                console.log(`Updated ${count} articles to add empty 'article' field.`);
            } else {
                console.log('All articles already have an article field.');
            }
        } catch (error) {
            console.error('Error updating articles:', error);
        }
    };


    const fetchArticleData = async (article) => {
        const pubDate = article.pubDate.toDate(); // Parse pubDate into a Date object
        updateLog({ currentTitle: article.title, date: pubDate });
        //console.log("article date:", pubDate);
        // Check if diffbotData already exists
        if (article.article && article.article.content) {
            updateLog({ action: "Article content already exists. Skipping fetch." });
            console.log('Article content already exists. Using existing diffbotData.');
            return; // Skip fetching
        }


        // Proceed with fetching the article content
        try {
            updateLog({ action: "Fetching article content from backend server" });

            // Make a request to your backend server
            console.log('Fetching url:', `https://article-extractor2.p.rapidapi.com/article/parse?url=${encodeURIComponent(article.link)}&rapidapi-key=8e93722614msh686dd9dfe65c1cfp1aa4cajsnf3c4ac3e3e94`);
            const response = await fetch(`https://article-extractor2.p.rapidapi.com/article/parse?url=${encodeURIComponent(article.link)}&rapidapi-key=8e93722614msh686dd9dfe65c1cfp1aa4cajsnf3c4ac3e3e94`);
            const extractedArticle = await response.json();

            if (extractedArticle.error) {
                console.log('Extraction failed, trying with archived URL.');
                updateLog({ action: "Extraction failed, trying with archived URL." });

                // Construct the archived URL
                const archivedUrl = `https://archive.is/newest/${article.link.split('?')[0]}`;

                try {
                    // Fetch extracted article using the archived URL
                    console.log('Fetching archived url:', `https://article-extractor2.p.rapidapi.com/article/proxy/parse?url=${encodeURIComponent(archivedUrl)}&rapidapi-key=8e93722614msh686dd9dfe65c1cfp1aa4cajsnf3c4ac3e3e94`);
                    const archivedResponse = await fetch(`https://article-extractor2.p.rapidapi.com/article/proxy/parse?url=${encodeURIComponent(archivedUrl)}&rapidapi-key=8e93722614msh686dd9dfe65c1cfp1aa4cajsnf3c4ac3e3e94`);
                    const archivedArticle = await archivedResponse.json();

                    if (archivedArticle.error) {
                        throw new Error(archivedArticle.error);
                    }

                    // Save the extracted archived article data to Firestore
                    await updateDoc(doc(db, 'articles', article.id), {
                        article: archivedArticle.data,
                        usedArchivedUrl: true
                    });

                    // Update the article object
                    article.article = archivedArticle.data;

                    updateLog({ action: "Article content saved to Firebase from archived URL" });
                    console.log('Article data extracted and saved from archived URL:', archivedArticle);

                } catch (archivedError) {
                    console.error('Error fetching article data from archived URL:', archivedError);
                    throw new Error(`Original Error: ${extractedArticle.error}; Archived Error: ${archivedError.message}`);
                }
            } else {

                // Save the extracted article data to Firestore
                await updateDoc(doc(db, 'articles', article.id), {
                    article: extractedArticle.data
                });

                // Update the article object
                article.article = extractedArticle.data;

                updateLog({ action: "Article content saved to Firebase" });
                console.log('Article data extracted and saved:', extractedArticle.data);
            }
        } catch (error) {
            console.error('Error fetching article data:', error);
            updateLog({ action: 'Error fetching article data.' });
            // Optionally, update Firestore to mark the article as having an error
            await updateDoc(doc(db, 'articles', article.id), {
                extractionError: error.message
            });
        }
    };

    const generateTags = async (article) => {
        // Check if diffbotData and article text are available
        if (
            !article.article ||
            !article.article.content
        ) {
            // Check for errorCode
            if (article.article && article.article.errorCode) {
                console.error(`Diffbot error code: ${article.diffbotData.errorCode}`);
                updateLog({ action: `Cannot generate tags and score due to Diffbot error code: ${article.article.errorCode}` });
                // Optionally, update Firestore to mark the article as having an error
                await updateDoc(doc(db, 'articles', article.id), {
                    tags: []
                });
            } else {
                console.error('No valid article text available for generating tags and score.');
                updateLog({ action: 'No valid article text available for generating tags and score.' });
            }
            return; // Exit the function
        }
        updateLog({ action: "Getting AI generated tags and score for article" });
        // Load the HTML string into Cheerio
        const $ = cheerio.load(article.article.content);

        // Extract text content without HTML tags
        const strippedString = $.text();

        const prompt = `Suggest maximum 3 tags (choose from adaptation, emissions, extremes, litigation, mitigation, opinion, policy, renewables, solution or research) for the following article:
            \n\n${strippedString} \n\n Limit your answer to only the tags, separated by a semicolon. The tag extremes is to be interpreted as 'extreme weather events', research as 'research and innovation', 
            and policy as 'policy and regulation'. In addition to these 3 tags, please score the article on climate change on a scale from 0 to 100 based on its scientific accuracy, relevance 
            to climate change, societal impact, policy influence, narrative framing, and overall engagement with the topic. Provide only the score and a brief comment explaining the result, separated by a semicolon.
            In addition to the tags and score, extract all explicitly mentioned geographic locations (countries, cities, regions) from the article. The location can be a city, a country or its capital, whatever is most relevant for this article but never respond 0,0 for lat,lon. 
            The output should be in this format: "tag 1;tag 2;tag 3;score;comment;latitude;longitude". Do not use a semicolon in the comment.`;
        console.log(prompt.split(0, 300));
        return openai.chat.completions.create({
            model: "gpt-4o-mini",
            messages: [
                {
                    "role": "user",
                    "content": [
                        {
                            "type": "text",
                            "text": prompt
                        }
                    ]
                }],
            temperature: 1,
            max_tokens: 100,
            top_p: 1,
            frequency_penalty: 0,
            presence_penalty: 0,
        })
            .then(response => {
                if (response.choices && response.choices.length > 0) {
                    const content = response.choices[0].message.content;

                    // Split the content by commas and trim each part
                    const parts = content.split(';').map(part => part.trim());

                    // Extract tags (first three parts)
                    const tags = parts.slice(0, 3);

                    // Extract score (fourth part)
                    const score = parts[3];

                    // Extract comment (remaining parts)
                    const comment = parts[4];

                    // Extract latitude and longitude
                    const lat = parseFloat(parts[5]);
                    const lng = parseFloat(parts[6]);

                    // Create a GeoPoint object
                    const geoPoint = new GeoPoint(lat, lng);

                    // Assign to article object
                    article.tags = tags;
                    article.score = score;
                    article.comment = comment;
                    article.geoPoint = geoPoint;

                    // Save data to Firestore
                    return updateDoc(doc(db, 'articles', article.id), {
                        tags,
                        score,
                        comment,
                        geoPoint
                    }).then(() => {
                        updateLog({ tags, score, comment });
                        console.log('Tags:', tags);
                        console.log('Score:', score);
                        console.log('Comment:', comment);
                        console.log('GeoPoint:', geoPoint);
                    });
                }
            })
            .catch(error => {
                console.error('Error generating tags and score:', error);
            });
    };

    const handleGenerateTLDR = async (article) => {
        // Check if diffbotData and article text are available
        if (
            !article.article ||
            !article.article.content
        ) {
            // Check for errorCode
            if (article.article && article.article.errorCode) {
                console.error(`Diffbot error code: ${article.article.errorCode}`);
                updateLog({ action: `Cannot generate TL;DR due to Diffbot error code: ${article.article.errorCode}` });
                // Optionally, update Firestore to mark the article as having an error
                await updateDoc(doc(db, 'articles', article.id), {
                    TLDR: "TL;DR not available"
                });
            } else {
                console.error('No valid article text available for generating TL;DR.');
                updateLog({ action: 'No valid article text available for generating TL;DR.' });
                await updateDoc(doc(db, 'articles', article.id), {
                    TLDR: "TL;DR not available"
                });
            }
            return; // Exit the function
        }

        updateLog({ action: "Generating TL;DR" });
        // Load the HTML string into Cheerio
        const $ = cheerio.load(article.article.content);

        // Extract text content without HTML tags
        const strippedString = $.text();

        const prompt = `Create a TL;DR that captures the essence of the article in two or three sentences, focusing on the main findings and implications without introductory phrases.\n\nArticle: ${article.title}\n\n${strippedString}`;
        console.log(prompt.split(0, 300));
        return openai.chat.completions.create({
            model: "gpt-4o-mini",
            messages: [
                {
                    "role": "user",
                    "content": [
                        {
                            "type": "text",
                            "text": prompt
                        }
                    ]
                }],
            temperature: 1,
            max_tokens: 120,
            top_p: 1,
            frequency_penalty: 0,
            presence_penalty: 0,
        })
            .then(response => {
                if (response.choices && response.choices.length > 0) {
                    const tldr = response.choices[0].message.content;
                    article.TLDR = tldr;
                    // Save TL;DR to Firestore
                    return updateDoc(doc(db, 'articles', article.id), {
                        TLDR: tldr
                    }).then(() => {
                        updateLog({ action: "Saving TL;DR to database", tldr: tldr });
                        console.log('TL;DR:', tldr);
                    });
                }
            })
            .catch(error => {
                console.error('Error generating TL;DR:', error);
            });
    };

    const processNextArticle = async () => {
        // Check if processing should continue
        if (!isProcessingRef.current) {
            updateLog({ action: 'Processing stopped by user' });
            return;
        }
        // Rate limiting logic
        const now = Date.now();
        const timeSinceLastCall = now - lastDiffbotCallTime;
        const minInterval = 8000; // 12 seconds in milliseconds

        if (timeSinceLastCall < minInterval) {
            const waitTime = minInterval - timeSinceLastCall;
            updateLog({ action: `Waiting for ${waitTime} ms to respect rate limit` });
            await delay(waitTime);
        }

        // Update the last call time
        lastDiffbotCallTime = Date.now();

        updateLog({ action: "Fetching next article from Firebase" });
        updateLog(prevLog => ({
            ...prevLog,
            processedCount: prevLog.processedCount + 1
        }));
        const articlesQuery = query(
            collection(db, 'articles'),
            where('TLDR', '==', ''),
            orderBy('pubDate', 'desc'),
            limit(1)
        );

        const querySnapshot = await getDocs(articlesQuery);
        if (querySnapshot.empty) {
            updateLog({ action: "No more articles to process" });
            setIsProcessing(false); // Stop processing
            return;
        }

        const articleDoc = querySnapshot.docs[0];
        const articleData = articleDoc.data();
        articleData.id = articleDoc.id;

        // Ensure diffbotData is included
        const article = {
            ...articleData,
            article: articleData.article || null
        };

        console.log(`Processing article: ${article.title}`);
        updateLog({ tags: [], tldr: '', score: '', comment: ''})
        fetchArticleData(article)
            .then(() => generateTags(article))
            .then(() => handleGenerateTLDR(article))
            .then(() => processNextArticle())
            .catch(error => {
                console.error('Error processing article:', error);
                processNextArticle();
            });
    };

    const startProcessing = () => {
        setLog({
            processedCount: 0,
            currentTitle: '',
            tags: [],
            tldr: '',
            action: '',
            date: new Date()
        });
        isProcessingRef.current = true; // Update the ref immediately
        setIsProcessing(true); // Start processing
        processNextArticle();
    };

    const stopProcessing = () => {
        setIsProcessing(false); // Stop processing
        isProcessingRef.current = false; // Update the ref immediately
        updateLog({ action: 'Processing will stop after current article' });
    };

    const addEmptyTLDRToArticles = async () => {
        try {
            // Reference to the articles collection
            const articlesRef = collection(db, 'articles');

            // Fetch all articles
            const articlesSnapshot = await getDocs(articlesRef);

            // Initialize a batch
            const batch = writeBatch(db);
            let count = 0;

            articlesSnapshot.forEach((doc) => {
                const data = doc.data();
                // Check if 'TLDR' field does not exist
                if (data.hasOwnProperty('TLDR') && data.TLDR === 'TL;DR not available') {
                    // Add an empty 'TLDR' field
                    batch.update(doc.ref, { 'TLDR': '' });
                    count++;
                }
            });

            if (count > 0) {
                // Commit the batch update
                await batch.commit();
                console.log(`Updated ${count} articles to add empty 'TLDR' field.`);
            } else {
                console.log('All articles already have a TLDR field.');
            }
        } catch (error) {
            console.error('Error updating articles:', error);
        }
    };

    const overwriteTLDRAndTags = async () => {
        try {
            // Define the cutoff date (October 29th)
            const cutoffDate = new Date('2024-11-1T00:00:00Z');

            // Reference to the articles collection
            const articlesRef = collection(db, 'articles');
            let lastVisible = null;
            let totalCount = 0;
            const batchSize = 500;

            while (true) {
                let articlesQuery = query(
                    articlesRef,
                    where('pubDate', '>', cutoffDate),
                    orderBy('pubDate'),
                    limit(batchSize)
                );

                if (lastVisible) {
                    articlesQuery = query(
                        articlesRef,
                        where('pubDate', '>', cutoffDate),
                        orderBy('pubDate'),
                        startAfter(lastVisible),
                        limit(batchSize)
                    );
                }

                // Fetch articles
                const querySnapshot = await getDocs(articlesQuery);

                if (querySnapshot.empty) {
                    console.log('No more articles to process.');
                    break;
                }

                // Initialize a batch
                const batch = writeBatch(db);
                let count = 0;

                querySnapshot.forEach((doc) => {
                    // Overwrite 'TLDR' and 'tags' fields with empty values
                    batch.update(doc.ref, {
                        'TLDR': '',
                        'tags': []
                    });
                    lastVisible = doc;
                    count++;
                });

                // Commit the batch update
                await batch.commit();
                totalCount += count;
                console.log(`Updated ${count} articles in this batch.`);

                // Check if we've processed all articles
                if (count < batchSize) {
                    break;
                }
            }

            console.log(`Total articles updated: ${totalCount}`);
        } catch (error) {
            console.error('Error updating articles:', error);
        }
    };

    return (
        <div>
            {!isProcessing ? (
                <button onClick={startProcessing} id="startprocessing">Start Processing Articles</button>
            ) : (
                <button onClick={stopProcessing} id="stopprocessing">Stop Processing</button>
            )}
            {' '}<button onClick={addEmptyTLDRToArticles} disabled>Update existing docs with empty TL;DR field</button>
            {' '}<button onClick={processLatestArticleWithoutContent} disabled>Extract articles by client</button>
            {' '}<button onClick={overwriteTLDRAndTags} disabled>Reset TLDR and Tags for Recent Articles</button>

            <div ref={logRef} style={{ height: '400px', overflowY: 'scroll', backgroundColor: 'black', color: 'white', padding: '10px', fontFamily: 'monospace' }}>
                <p>Articles Processed: {log.processedCount}</p>
                <p>Current Article: {log.currentTitle}</p>
                <p>Date: {log.date ? format(log.date, 'dd-MM-yyyy HH:mm') : 'none'}</p>
                <p>Generated tags: {log.tags.join(', ')}</p>
                <p>Score and comment: {log.score} {log.comment}</p>
                <p>Generated TL:DR: {log.tldr.split(0, 50)}</p>
                <p>Action: {log.action}</p>
            </div>
            {log.action === 'No more articles to process' && <div id="doneProcessing">All articles processed</div>}
        </div>
    );
};

export default ProcessArticles;